import {ChangeDetectorRef, Component, HostBinding, inject, input, OnInit, signal} from '@angular/core';
import {StoryblokStylingService} from '@core/services/storyblok-styling.service';
import {StoryblokDirective} from '@core/directives/storyblok.directive';
import {DynamicModule} from 'ng-dynamic-component';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormValidationClassDirective} from '@core/directives/form-validation-class.directive';
import {LoadingComponent} from '@layout/components/general/loading/loading.component';
import {DeliveryService} from '@core/services/delivery.service';
import {environment} from '@env/environment';

type DeliveryCheckState = 'idle' | 'loading' | 'success' | 'error';

@Component({
  selector: 'app-check-postcode',
  templateUrl: './storyblok-check-postcode.component.html',
  styleUrls: ['./storyblok-check-postcode.component.scss'],
  imports: [
    StoryblokDirective,
    DynamicModule,
    ReactiveFormsModule,
    FormValidationClassDirective,
    LoadingComponent
  ]
})
export class StoryblokCheckPostcodeComponent implements OnInit {
  private storyblokStylingService = inject(StoryblokStylingService);
  private deliveryService = inject(DeliveryService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly _editable = input<any>();
  readonly general_styling = input<any>();
  @HostBinding('class') class: any;

  state = signal<DeliveryCheckState>('idle');
  errorMessage = signal<string | undefined>(undefined);

  formGroup: FormGroup<{
    postcode: FormControl<string | null>;
  }>;

  constructor() {
    this.formGroup = new FormGroup({
      postcode: new FormControl('', {
        validators: [
          Validators.pattern('^[0-9]{3} ?[0-9]{2}$'), // Swedish postcode: 3 digits, optional space, 2 digits (e.g., "123 45" or "12345")
          Validators.required
        ]
      })
    });

    // Reset state when user types in the postcode field
    this.formGroup.controls.postcode.valueChanges.subscribe(() => {
      if (this.state() !== 'idle') {
        this.state.set('idle');
        this.errorMessage.set(undefined);
      }
    });
  }

  ngOnInit() {
    this.class = this.storyblokStylingService.getClassList(
      this.general_styling(),
      {
        desktop: {
          'margin-bottom': '.5rem',
          'margin-top': '.5rem'
        }
      },
      {'col-': 12}
    );
  }

  async formSubmit() {
    if (!this.formGroup.value.postcode) {
      return;
    }

    if (this.formGroup.valid) {
      this.state.set('loading');
      this.errorMessage.set(undefined);

      try {
        const countryCode = environment.checkout.defaultCountry;
        // Remove spaces from postcode before sending to API (Swedish postcodes can be entered as "123 45" or "12345")
        const postcodeWithoutSpaces = this.formGroup.value.postcode.replace(' ', '');
        
        const result = await this.deliveryService.isPostcodeDeliverable(
          postcodeWithoutSpaces,
          countryCode
        );

        if (result?.isDeliverable) {
          this.state.set('success');
        } else {
          this.state.set('error');
          if (result?.errors && result.errors.length > 0) {
            this.errorMessage.set(result.errors[0] || undefined);
          }
        }
      } catch (error) {
        this.state.set('error');
        this.errorMessage.set('Ett fel uppstod vid kontroll av postnumret.');
      } finally {
        this.changeDetectorRef.detectChanges();
      }
    }
  }
}
